import { MODEL_MAX_BYTES } from "@divespots/contracts";
import type { DiveObject, ObjectModel } from "@divespots/contracts";
import { useQuery } from "@tanstack/react-query";
import { Box, Trash2, Upload } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from "react";
import type { DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.ts";
import { isApiRequestError } from "../../api/errors.ts";
import { keys, useObjectModelMutations, useObjectModels } from "../../api/hooks.ts";
import { Button } from "../../components/Button.tsx";
import { formatNumber } from "../../i18n/format.ts";
import { useLocale } from "../../i18n/index.ts";
import { useSession } from "../session.ts";
import { ConfirmSheet, Field } from "../ui.tsx";
import { formatBytes, precheckModelFile } from "./glb.ts";

// three.js, GLTFLoader and the meshopt decoder: only fetched when a preview is shown
const ModelPreview = lazy(() => import("./ModelPreview.tsx"));

interface Pending {
  name: string;
  buffer: ArrayBuffer;
}

/**
 * The 3D model (.glb) of one saved object. Admins upload, replace, edit and delete; moderators see
 * it read-only. The server validates and is authoritative: its message is shown as it is.
 */
export function ObjectModelSection({
  object,
  onDirtyChange,
}: {
  object: Pick<DiveObject, "id" | "spotId">;
  /** Unsaved model edits (a chosen file, changed fields): the host guards leaving like its own form. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { user } = useSession();
  const isAdmin = user?.role === "admin";
  const models = useObjectModels({ spotId: object.spotId });
  const model = models.data?.find((m) => m.objectId === object.id) ?? null;
  const m = useObjectModelMutations(object.id);
  const uid = useId();
  const input = useRef<HTMLInputElement | null>(null);

  const [pending, setPending] = useState<Pending | null>(null);
  const [attribution, setAttribution] = useState("");
  const [scale, setScale] = useState("1");
  const [heading, setHeading] = useState("0");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ kind: "error" | "ok"; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  // the form follows the stored model (first load, and after a save or a replace)
  const stamp = model?.updatedAt ?? "";
  useEffect(() => {
    setAttribution(model?.attribution ?? "");
    setScale(String(model?.scale ?? 1));
    setHeading(String(model?.headingDeg ?? 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp, model === null]);

  // the stored file, for the preview (a chosen file previews itself)
  const stored = useQuery({
    queryKey: keys.objectModelFile(model?.sha256),
    queryFn: ({ signal }) => api.getObjectModelFile(`${model?.sha256}.glb`, signal),
    enabled: Boolean(model) && !pending,
    retry: false,
    staleTime: Infinity,
  });
  const previewData = pending?.buffer ?? stored.data ?? null;
  const scaleNum = Number(scale);
  const headingNum = Number(heading);
  const previewScale = scaleNum >= 0.01 && scaleNum <= 100 ? scaleNum : (model?.scale ?? 1);
  const previewHeading =
    headingNum >= 0 && headingNum < 360 ? headingNum : (model?.headingDeg ?? 0);
  const onPreviewError = useCallback(() => setPreviewFailed(true), []);
  useEffect(() => setPreviewFailed(false), [previewData]);

  const serverMessage = (e: unknown): string => {
    if (!isApiRequestError(e)) return t("admin.err.save");
    switch (e.code) {
      case "payload_too_large":
        return t("admin.model.err.tooLarge");
      case "forbidden":
      case "unauthorized":
        return t("admin.model.err.forbidden");
      case "rate_limited":
        return t("admin.model.err.rateLimited");
      case "not_found":
        return t("admin.model.err.notFound");
      case "bad_request":
      case "conflict":
        return e.message; // the server names the rule that failed; shown verbatim
      default:
        return t("admin.err.save");
    }
  };

  async function choose(file: File | undefined) {
    if (!file) return;
    setMessage(null);
    const check = precheckModelFile(file, await file.slice(0, 12).arrayBuffer());
    if (!check.ok) {
      setPending(null);
      setMessage({ kind: "error", text: t(`admin.model.err.${check.reason}`) });
      return;
    }
    setPending({ name: file.name, buffer: await file.arrayBuffer() });
  }

  function validate(): { attribution: string; scale: number; headingDeg: number } | null {
    const errs: Record<string, string> = {};
    const a = attribution.trim();
    if (!a) errs.attribution = t("admin.err.required");
    if (!(scaleNum >= 0.01 && scaleNum <= 100)) errs.scale = t("admin.model.err.scale");
    if (heading.trim() === "" || !(headingNum >= 0 && headingNum < 360))
      errs.heading = t("admin.model.err.heading");
    setErrors(errs);
    return Object.keys(errs).length > 0
      ? null
      : { attribution: a, scale: scaleNum, headingDeg: headingNum };
  }

  async function save() {
    setMessage(null);
    const v = validate();
    if (!v) return;
    try {
      if (pending) {
        await m.upload.mutateAsync({ file: pending.buffer, ...v });
        setPending(null);
        setMessage({ kind: "ok", text: t("admin.model.uploaded") });
      } else if (model) {
        await m.update.mutateAsync(v);
        setMessage({ kind: "ok", text: t("admin.model.saved") });
      }
    } catch (e) {
      setMessage({ kind: "error", text: serverMessage(e) });
    }
  }

  async function remove() {
    try {
      await m.remove.mutateAsync();
      setConfirmDelete(false);
      setPending(null);
      setMessage({ kind: "ok", text: t("admin.model.deleted") });
    } catch (e) {
      setConfirmDelete(false);
      setMessage({ kind: "error", text: serverMessage(e) });
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void choose(e.dataTransfer.files[0]);
  };

  const busy = m.upload.isPending || m.update.isPending || m.remove.isPending;
  const dirty =
    pending !== null ||
    (model !== null &&
      (attribution.trim() !== model.attribution ||
        scaleNum !== model.scale ||
        headingNum !== model.headingDeg));
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  return (
    <section className="adm-subform adm-model" aria-labelledby={`${uid}-h`}>
      <h3 id={`${uid}-h`} className="ds-text-title-3 m-0">
        {t("admin.model.title")}
      </h3>

      {models.isPending ? <p className="m-0">{t("common.loading")}</p> : null}
      {models.isError ? (
        <p className="adm-error m-0" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}

      {model ? <ModelFacts model={model} locale={locale} /> : null}
      {!model && !pending && models.isSuccess ? (
        <p className="ds-text-subheadline text-label-secondary m-0">{t("admin.model.none")}</p>
      ) : null}

      {isAdmin ? (
        <>
          <div
            className="adm-model__drop"
            data-dragging={dragging ? "true" : undefined}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <Upload aria-hidden="true" size={20} strokeWidth={1.75} />
            <p className="ds-text-subheadline m-0">
              {pending
                ? t("admin.model.chosen", {
                    name: pending.name,
                    size: formatBytes(pending.buffer.byteLength),
                  })
                : t("admin.model.drop", { max: MODEL_MAX_BYTES / 1024 / 1024 })}
            </p>
            <input
              ref={input}
              type="file"
              className="sr-only"
              accept=".glb,model/gltf-binary"
              aria-label={t("admin.model.file")}
              data-testid="model-file"
              onChange={(e) => {
                void choose(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button variant="tinted" onClick={() => input.current?.click()}>
              {model || pending ? t("admin.model.replace") : t("admin.model.choose")}
            </Button>
          </div>

          {model || pending ? (
            <>
              <Field
                label={t("admin.model.attribution")}
                hint={t("admin.model.attributionHint")}
                error={errors.attribution}
              >
                {({ id, describedBy, invalid }) => (
                  <input
                    id={id}
                    className="adm-input"
                    maxLength={300}
                    value={attribution}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    aria-required="true"
                    onChange={(e) => setAttribution(e.target.value)}
                  />
                )}
              </Field>
              <div className="adm-model__pair">
                <Field label={t("admin.model.scale")} error={errors.scale}>
                  {({ id, describedBy, invalid }) => (
                    <input
                      id={id}
                      className="adm-input"
                      type="number"
                      inputMode="decimal"
                      min={0.01}
                      max={100}
                      step="any"
                      value={scale}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      onChange={(e) => setScale(e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t("admin.model.heading")}
                  hint={t("admin.model.headingHint")}
                  error={errors.heading}
                >
                  {({ id, describedBy, invalid }) => (
                    <input
                      id={id}
                      className="adm-input"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="any"
                      value={heading}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      onChange={(e) => setHeading(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {previewData && !previewFailed ? (
        <Suspense fallback={<p role="status">{t("admin.model.previewLoading")}</p>}>
          <div role="img" aria-label={t("admin.model.preview")} className="adm-model__frame">
            <ModelPreview
              data={previewData}
              scale={previewScale}
              headingDeg={previewHeading}
              northLabel={t("admin.model.north")}
              onError={onPreviewError}
            />
          </div>
        </Suspense>
      ) : null}
      {previewFailed ? (
        <p className="ds-text-footnote text-label-secondary m-0" role="status">
          {t("admin.model.previewFailed")}
        </p>
      ) : null}

      {message ? (
        <p
          className={`${message.kind === "error" ? "adm-error" : "adm-ok"} m-0`}
          role={message.kind === "error" ? "alert" : "status"}
          data-testid="model-message"
        >
          {message.text}
        </p>
      ) : null}

      {isAdmin && (model || pending) ? (
        <div className="adm-actions">
          <Button
            variant="filled-lake"
            icon={<Upload aria-hidden="true" strokeWidth={1.75} />}
            disabled={busy || !dirty}
            onClick={() => void save()}
          >
            {pending ? t("admin.model.upload") : t("admin.model.save")}
          </Button>
          {pending ? (
            <Button variant="tinted" disabled={busy} onClick={() => setPending(null)}>
              {t("admin.model.discard")}
            </Button>
          ) : null}
          {model && !pending ? (
            <Button
              variant="destructive"
              icon={<Trash2 aria-hidden="true" strokeWidth={1.75} />}
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              {t("admin.model.remove")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {confirmDelete ? (
        <ConfirmSheet
          title={t("admin.model.removeTitle")}
          body={t("admin.model.removeBody")}
          confirmLabel={t("admin.common.delete")}
          busy={m.remove.isPending}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </section>
  );
}

function ModelFacts({
  model,
  locale,
}: {
  model: ObjectModel;
  locale: ReturnType<typeof useLocale>;
}) {
  const { t } = useTranslation();
  const n = (v: number) => formatNumber(v, locale, 2);
  return (
    <dl className="adm-model__facts ds-text-subheadline" data-testid="model-facts">
      <dt>
        <Box aria-hidden="true" size={14} strokeWidth={1.75} /> {t("admin.model.size")}
      </dt>
      <dd>
        {formatBytes(model.sizeBytes)} · {formatNumber(model.triangles, locale, 0)}{" "}
        {t("admin.model.triangles")}
      </dd>
      <dt>{t("admin.model.bounds")}</dt>
      <dd>
        {n(model.boundsM.x)} × {n(model.boundsM.y)} × {n(model.boundsM.z)} m
      </dd>
      {model.animations.length > 0 ? (
        <>
          <dt>{t("admin.model.animations")}</dt>
          <dd>{model.animations.join(", ")}</dd>
        </>
      ) : null}
      <dt>{t("admin.model.attribution")}</dt>
      <dd>{model.attribution}</dd>
      <dt>
        {t("admin.model.scale")} · {t("admin.model.headingShort")}
      </dt>
      <dd>
        ×{n(model.scale)} · {formatNumber(model.headingDeg, locale, 0)}°
      </dd>
    </dl>
  );
}
